import React from 'react';
import { ModalX } from '../../../components';
import { useGetRentBreakdown } from '../../../custom-hooks/use-stats';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Link } from 'react-router-dom';
import { Spinner } from '@material-tailwind/react';

const SeatsDetailModal = ({ isOpen, onClose }) => {

    // Custom hooks
    const {
        data: rentBreakdown,
        isLoading: isRentBreakdownLoading,
        error: rentBreakdownError,
    } = useGetRentBreakdown();

    const renderUI = () => {
        if (isRentBreakdownLoading) {
            return <div className='h-[400px] flex justify-center items-center'>
                <Spinner className="h-8 w-8" color="blue" />
            </div>;
        }
        if (rentBreakdownError) {
            return <div className="p-8 text-center text-red-500">Error: {rentBreakdownError.message}</div>;
        }

        // Filter out customers with 0 seats and 0 offices initially, or just show those with seats/offices.
        const validCustomers = rentBreakdown.customers.filter(
            ({ stats }) => stats.occupiedSeatsCount > 0 || stats.occupiedOfficesCount > 0
        );

        // Sort by occupied seats count
        const sortedCustomers = [...validCustomers].sort(
            ({ stats: next }, { stats: prev }) => prev.occupiedSeatsCount - next.occupiedSeatsCount
        );

        // Pie chart data: we represent the number of occupied seats.
        const data = sortedCustomers.filter(({ stats }) => stats.occupiedSeatsCount > 0).map((customer) => ({
            name: customer.name,
            value: customer.stats.occupiedSeatsCount,
        }));

        const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f43f5e', '#0ea5e9'];

        const CustomPieTooltip = ({ active, payload }) => {
            if (active && payload && payload.length) {
                return (
                    <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-100 flex flex-col gap-1">
                        <p className="font-medium text-sm text-gray-700 flex items-center gap-2">
                            <span
                                className="w-3 h-3 rounded-full inline-block"
                                style={{ backgroundColor: payload[0].payload.fill }}
                            ></span>
                            {payload[0].name}
                        </p>
                        <p className="text-xl font-semibold text-gray-800 ml-5">
                            {payload[0].value} <span className="text-sm font-normal text-gray-500">Seats</span>
                        </p>
                    </div>
                );
            }
            return null;
        };

        return (
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-8 p-1 py-4'>
                <div className='w-full h-[350px] lg:h-[500px] bg-gray-50/50 rounded-2xl flex items-center justify-center p-4 border border-gray-100'>
                    {data.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="65%"
                                    outerRadius="85%"
                                    paddingAngle={2}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} className="focus:outline-none" />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomPieTooltip />} cursor={{ fill: 'transparent' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className='flex flex-col items-center justify-center text-gray-400'>
                            <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                            </svg>
                            <span className="text-sm font-medium">No occupancy data</span>
                        </div>
                    )}
                </div>

                <div className='lg:col-span-2'>
                    <div className='bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col h-[400px] lg:h-[500px]'>
                        <div className='overflow-x-auto overflow-y-auto'>
                            <table className='w-full text-left border-collapse min-w-max'>
                                <thead className='bg-gray-50/80 sticky top-0 z-10 backdrop-blur-sm'>
                                    <tr>
                                        <th className='text-xs font-semibold text-gray-500 uppercase tracking-wider py-4 px-6 border-b border-gray-100'>Customer</th>
                                        <th className='text-xs font-semibold text-gray-500 uppercase tracking-wider py-4 px-6 border-b border-gray-100 text-right'>Seats</th>
                                        <th className='text-xs font-semibold text-gray-500 uppercase tracking-wider py-4 px-6 border-b border-gray-100 text-right'>Offices</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-gray-50'>
                                    {sortedCustomers.map((customer) => (
                                        <tr key={customer.name} className='hover:bg-gray-50/70 transition-colors group'>
                                            <td className='py-3 px-6'>
                                                <Link
                                                    to={`/customer/${customer._id}`}
                                                    className='text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors'
                                                    target='_blank'
                                                >
                                                    {customer.name}
                                                </Link>
                                            </td>
                                            <td className='py-3 px-6 text-right'>
                                                <div className='inline-flex items-center justify-center text-gray-800 text-sm font-semibold'>
                                                    {customer.stats.occupiedSeatsCount}
                                                </div>
                                            </td>
                                            <td className='py-3 px-6 text-right'>
                                                <div className='inline-flex items-center justify-center text-gray-800 text-sm font-semibold'>
                                                    {customer.stats.occupiedOfficesCount}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {sortedCustomers.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className='text-center py-12 text-sm text-gray-500'>
                                                No occupancy data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <ModalX title='Seats Occupancy Details' isOpen={isOpen} onClose={onClose} maxWidth='xxxl'>
            {renderUI()}
        </ModalX>
    )
};

export default SeatsDetailModal;
